/**
 * Parser do CSV exportado pela AWS Pricing Calculator.
 *
 * Porte de `handleCsvFile()` de `construtorps.html`, com três diferenças deliberadas:
 *
 * 1. Não depende do PapaParse. O núcleo é livre de dependências (decisão D3), então o tokenizador
 *    é próprio. É um subconjunto suficiente do RFC 4180: campos entre aspas, aspas escapadas por
 *    duplicação, e quebras de linha dentro de campo entre aspas.
 * 2. Devolve erros tipados em vez de escrever mensagem em elemento do DOM.
 * 3. Acumula avisos por linha ignorada, em vez de interromper silenciosamente.
 *
 * Requisito preservado do legado: a exportação precisa estar em **inglês**. O cabeçalho é
 * localizado pela coluna `Group hierarchy`, que em português vem traduzida e não é encontrada.
 */

import { CONFIGURACAO_PADRAO, type Configuracao } from './configuracao.ts';
import { especificacaoDe } from './especificacoes-instancia.ts';
import type { ItemCusto } from './tipos.ts';

// ---------------------------------------------------------------------------
// Contrato
// ---------------------------------------------------------------------------

/**
 * Marcador gravado em `ItemCusto.descricao` quando a coluna `Description` vem vazia.
 *
 * Existe porque o schema exige texto não vazio. Não deve chegar ao cliente: os renderizadores
 * passam a descrição por `descricaoExibivel()` antes de imprimir.
 */
export const SEM_DESCRICAO = 'N/A';

/**
 * Descrição do item pronta para exibição, ou string vazia quando o CSV não trouxe descrição.
 *
 * A coluna `Description` da calculadora é preenchida pelo arquiteto ao montar a estimativa, e é o
 * que dá contexto de negócio ao item — `Servidor de aplicação` diz mais ao cliente que
 * `Amazon EC2`. Quando está vazia, o legado imprimia `N/A` na proposta; aqui a célula fica vazia.
 */
export function descricaoExibivel(descricao: string): string {
  const texto = descricao.trim();
  return texto === '' || texto.toUpperCase() === SEM_DESCRICAO ? '' : texto;
}

/** Colunas exigidas na exportação. A ausência de qualquer uma invalida o arquivo. */
export const COLUNAS_OBRIGATORIAS = [
  'Service',
  'Monthly',
  'Configuration summary',
  'Description',
  'Region',
  'Group hierarchy',
] as const;

export interface ResultadoCsv {
  /** Região lida da primeira linha de dados, como no legado. `null` quando não informada. */
  readonly regiao: string | null;
  readonly itens: readonly ItemCusto[];
  /** Soma dos custos líquidos, em USD, antes de qualquer margem. */
  readonly custoLiquidoTotalUsd: number;
  /** Linhas de dados examinadas, incluindo as descartadas. */
  readonly linhasExaminadas: number;
  /** Linhas descartadas por custo mensal negativo. Item de custo zero é preservado. */
  readonly linhasDescartadas: number;
  readonly avisos: readonly string[];
}

export type CodigoErroCsv =
  | 'ARQUIVO_VAZIO'
  | 'CABECALHO_NAO_ENCONTRADO'
  | 'COLUNA_OBRIGATORIA_AUSENTE'
  | 'NENHUM_ITEM_VALIDO';

export class ErroCsv extends Error {
  readonly codigo: CodigoErroCsv;
  readonly detalhe: Readonly<Record<string, unknown>>;

  constructor(
    codigo: CodigoErroCsv,
    mensagem: string,
    detalhe: Readonly<Record<string, unknown>> = {},
  ) {
    super(mensagem);
    this.name = 'ErroCsv';
    this.codigo = codigo;
    this.detalhe = detalhe;
  }
}

// ---------------------------------------------------------------------------
// Tokenizador
// ---------------------------------------------------------------------------

/**
 * Converte o texto do CSV em matriz de campos.
 *
 * Trata `\r\n`, `\n` e `\r` como fim de linha, remove o BOM, e preserva quebras de linha que
 * estejam dentro de campo entre aspas — caso real na coluna `Configuration summary`.
 */
export function tokenizarCsv(texto: string): string[][] {
  const conteudo = texto.charCodeAt(0) === 0xfeff ? texto.slice(1) : texto;

  const linhas: string[][] = [];
  let campos: string[] = [];
  let campo = '';
  let entreAspas = false;

  for (let i = 0; i < conteudo.length; i += 1) {
    // `charAt` devolve string sempre, o que evita a indexação possivelmente indefinida.
    const c = conteudo.charAt(i);

    if (entreAspas) {
      if (c === '"') {
        if (conteudo.charAt(i + 1) === '"') {
          campo += '"';
          i += 1;
        } else {
          entreAspas = false;
        }
      } else {
        campo += c;
      }
      continue;
    }

    if (c === '"') {
      entreAspas = true;
    } else if (c === ',') {
      campos.push(campo);
      campo = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && conteudo.charAt(i + 1) === '\n') i += 1;
      campos.push(campo);
      linhas.push(campos);
      campos = [];
      campo = '';
    } else {
      campo += c;
    }
  }

  // Última linha sem terminador.
  if (campo !== '' || campos.length > 0) {
    campos.push(campo);
    linhas.push(campos);
  }

  return linhas;
}

// ---------------------------------------------------------------------------
// Enriquecimento da descrição
// ---------------------------------------------------------------------------

const PADRAO_INSTANCIA =
  /(?:Instance Type|Instance class|Advance EC2 instance|DB Instance Class)\s*\(([^)]+)\)/i;
const PADRAO_ARMAZENAMENTO = /(?:Storage amount|EBS Storage amount)\s*\(([^)]+)\)/i;
const PADRAO_RESERVA = /Savings Plans|Reserved/i;
// A calculadora alterna entre `On-Demand`, `On Demand` e `OnDemand` conforme o serviço. Sem
// tolerar as três formas, itens de RDS ficavam sem o modelo de compra na proposta.
const PADRAO_SOB_DEMANDA = /On[-\s]?Demand/i;
/*
 * Duas formas convivem na mesma exportação:
 *   EC2 — `Pricing strategy (On-Demand Utilization: 100 %Utilized/Month)`
 *   RDS — `Utilization (On-Demand only) (100 %Utilized/Month)`
 * O intervalo entre o rótulo e o número é limitado para não capturar um percentual de outro
 * fragmento da configuração.
 */
const PADRAO_UTILIZACAO = /Utilization[^%]{0,40}?([\d.,]+)\s*%/i;
/*
 * Quantidade de instâncias do item. A calculadora declara de duas formas, e ignorá-la fazia a
 * memória de cálculo descrever uma única máquina num item que cobra por várias.
 *   `Workload (Consistent, Number of instances: 2)`
 *   `Nodes (3)`
 */
const PADRAO_QUANTIDADE = /Number of (?:instances|nodes)\s*:\s*(\d+)/i;
const PADRAO_NOS = /\bNodes\s*\(\s*(\d+)\s*\)/i;

function ehComputacao(servico: string): boolean {
  const s = servico.toLowerCase();
  return s.includes('ec2') || s.includes('rds');
}

/** Quantidade de instâncias declarada na configuração. `1` quando não há declaração explícita. */
function extrairQuantidade(configuracaoBruta: string): number {
  const bruto =
    PADRAO_QUANTIDADE.exec(configuracaoBruta)?.[1] ?? PADRAO_NOS.exec(configuracaoBruta)?.[1];
  if (bruto === undefined) return 1;

  const valor = Number.parseInt(bruto, 10);
  return Number.isFinite(valor) && valor > 0 ? valor : 1;
}

/**
 * Monta o resumo legível da configuração a partir do campo bruto da calculadora.
 *
 * Ordem dos fragmentos, preservada do legado: especificação da instância, armazenamento, modelo de
 * compra e utilização. Quando nenhum fragmento é reconhecido, devolve o campo original.
 */
export function resumirConfiguracao(
  configuracaoBruta: string,
  servico: string,
  baseHorasMensais: number,
): string {
  const partes: string[] = [];

  const instancia = PADRAO_INSTANCIA.exec(configuracaoBruta);
  if (instancia?.[1] !== undefined) {
    const tipo = instancia[1].trim();
    const especificacao = especificacaoDe(tipo) ?? tipo;
    const quantidade = extrairQuantidade(configuracaoBruta);
    partes.push(quantidade > 1 ? `${quantidade} instâncias de ${especificacao}` : especificacao);
  }

  const armazenamento = PADRAO_ARMAZENAMENTO.exec(configuracaoBruta);
  if (armazenamento?.[1] !== undefined) {
    const bruto = armazenamento[1].trim();
    if (ehComputacao(servico)) {
      const quantidade = Number.parseFloat(bruto);
      if (Number.isFinite(quantidade)) {
        const unidade = bruto.replace(String(quantidade), '').trim();
        partes.push(`${quantidade} ${unidade} SSD`.replace(/\s+/g, ' ').trim());
      } else {
        partes.push(bruto);
      }
    } else {
      partes.push(bruto);
    }
  }

  if (PADRAO_RESERVA.test(configuracaoBruta)) {
    partes.push('Plano de Reserva/Economia');
  } else if (PADRAO_SOB_DEMANDA.test(configuracaoBruta)) {
    partes.push('On-Demand');
  }

  const utilizacao = PADRAO_UTILIZACAO.exec(configuracaoBruta);
  if (utilizacao?.[1] !== undefined) {
    const percentual = Number.parseFloat(utilizacao[1].replace(',', '.'));
    if (Number.isFinite(percentual)) {
      // Em EC2 a utilização é traduzida em horas, que é o que o cliente entende.
      // Nos demais serviços, o percentual é exibido cru.
      partes.push(
        servico.toLowerCase().includes('ec2')
          ? `${Math.round(baseHorasMensais * (percentual / 100))} horas`
          : `${utilizacao[1]}% Uso`,
      );
    }
  }

  return partes.length > 0 ? partes.join(', ') : configuracaoBruta;
}

/** Último nível da hierarquia de grupos. `A > B > C` devolve `C`. */
export function extrairGrupo(hierarquia: string): string {
  const bruto = hierarquia.trim();
  if (bruto === '') return 'Sem Grupo';

  const niveis = bruto.split('>');
  const ultimo = niveis[niveis.length - 1]?.trim();
  return ultimo === undefined || ultimo === '' ? bruto : ultimo;
}

/**
 * Interpreta o custo mensal.
 *
 * A calculadora exporta em formato norte-americano. Separador de milhar é removido e o cifrão,
 * quando presente, é ignorado.
 */
export function interpretarCusto(bruto: string): number {
  const limpo = bruto.replace(/[$\s]/g, '').replace(/,/g, '');
  const valor = Number.parseFloat(limpo);
  return Number.isFinite(valor) ? valor : 0;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/**
 * Interpreta o CSV da AWS Pricing Calculator.
 *
 * @throws {ErroCsv} quando o arquivo está vazio, o cabeçalho não é localizado, falta coluna
 * obrigatória, ou nenhuma linha de item é encontrada.
 */
export function analisarCsvCalculadora(
  texto: string,
  config: Configuracao = CONFIGURACAO_PADRAO,
): ResultadoCsv {
  const avisos: string[] = [];
  const linhas = tokenizarCsv(texto);

  if (linhas.length === 0 || texto.trim() === '') {
    throw new ErroCsv('ARQUIVO_VAZIO', 'O arquivo enviado está vazio.');
  }

  // O cabeçalho é a primeira linha que contém a coluna `Group hierarchy`. A calculadora emite
  // metadados antes dele, e a quantidade de linhas varia entre versões da exportação.
  const indiceCabecalho = linhas.findIndex((linha) =>
    linha.some((celula) => celula.trim() === 'Group hierarchy'),
  );

  if (indiceCabecalho === -1) {
    throw new ErroCsv(
      'CABECALHO_NAO_ENCONTRADO',
      'Cabeçalho não localizado: a coluna "Group hierarchy" não existe no arquivo. ' +
        'A exportação da AWS Pricing Calculator precisa estar no idioma inglês.',
    );
  }

  const cabecalho = linhas[indiceCabecalho] ?? [];
  const colunas = new Map<string, number>();
  cabecalho.forEach((nome, indice) => {
    const chave = nome.trim();
    if (chave !== '' && !colunas.has(chave)) colunas.set(chave, indice);
  });

  const ausentes = COLUNAS_OBRIGATORIAS.filter((coluna) => !colunas.has(coluna));
  if (ausentes.length > 0) {
    throw new ErroCsv(
      'COLUNA_OBRIGATORIA_AUSENTE',
      `Coluna obrigatória ausente no CSV: ${ausentes.join(', ')}. ` +
        'A exportação da AWS Pricing Calculator precisa estar no idioma inglês.',
      { ausentes, encontradas: [...colunas.keys()] },
    );
  }

  const ler = (linha: readonly string[], coluna: string): string => {
    const indice = colunas.get(coluna);
    return indice === undefined ? '' : (linha[indice] ?? '').trim();
  };

  const itens: ItemCusto[] = [];
  let regiao: string | null = null;
  let custoLiquidoTotalUsd = 0;
  let linhasExaminadas = 0;
  let linhasDescartadas = 0;

  for (let i = indiceCabecalho + 1; i < linhas.length; i += 1) {
    const linha = linhas[i];
    if (linha === undefined) continue;

    const servico = ler(linha, 'Service');

    // O legado interrompe na primeira linha sem serviço, o que corresponde ao rodapé da
    // exportação. Aqui a linha é ignorada e a varredura continua, para não perder itens quando
    // houver linha em branco no meio do arquivo.
    if (servico === '') continue;

    linhasExaminadas += 1;

    if (regiao === null) {
      const bruta = ler(linha, 'Region');
      if (bruta !== '') regiao = bruta;
    }

    /*
     * Item de custo zero é preservado. O legado descartava, e isso produzia proposta em que faltavam
     * serviços que o arquiteto dimensionou de propósito — transferência de dados em franquia gratuita
     * é o caso comum, e é justamente o que o cliente quer ver declarado, porque a dúvida "quanto vou
     * pagar de tráfego" aparece em toda migração. Item ausente da relação parece esquecimento; item
     * listado com zero é resposta.
     *
     * Custo negativo continua descartado: `itemCusto.custoMensalLiquidoUsd` é `nonnegative` no schema
     * e `precificar` recusa item negativo, então aceitá-lo aqui só adiaria a falha para a publicação.
     */
    const custoMensalLiquidoUsd = interpretarCusto(ler(linha, 'Monthly'));
    if (custoMensalLiquidoUsd < 0) {
      linhasDescartadas += 1;
      continue;
    }

    itens.push({
      servico,
      descricao: ler(linha, 'Description') || SEM_DESCRICAO,
      resumoConfiguracao: resumirConfiguracao(
        ler(linha, 'Configuration summary'),
        servico,
        config.baseHorasMensais,
      ),
      grupo: extrairGrupo(ler(linha, 'Group hierarchy')),
      custoMensalLiquidoUsd,
    });

    custoLiquidoTotalUsd += custoMensalLiquidoUsd;
  }

  if (itens.length === 0) {
    throw new ErroCsv('NENHUM_ITEM_VALIDO', 'Nenhum item de custo foi encontrado no CSV.', {
      linhasExaminadas,
      linhasDescartadas,
    });
  }

  if (linhasDescartadas > 0) {
    avisos.push(
      `${linhasDescartadas} ${linhasDescartadas === 1 ? 'linha foi ignorada' : 'linhas foram ignoradas'} ` +
        'por ter custo mensal negativo.',
    );
  }

  // Estimativa inteira zerada é quase sempre exportação da calculadora antes de configurar os
  // serviços. Avisar em vez de recusar: quem monta a proposta é quem sabe se o zero é intencional.
  if (custoLiquidoTotalUsd === 0) {
    avisos.push(
      'Todos os itens importados têm custo mensal zero. Confira se a estimativa da calculadora ' +
        'estava completa antes de publicar.',
    );
  }

  if (regiao === null) {
    avisos.push('A região não foi informada em nenhuma linha do CSV.');
  }

  return {
    regiao,
    itens,
    custoLiquidoTotalUsd,
    linhasExaminadas,
    linhasDescartadas,
    avisos,
  };
}
